import { type Box3, Plane, Vector3 } from 'three';

/**
 * Section axes are expressed in model terms (IFC is Z-up) while the scene is
 * three.js Y-up: model X -> three X, model Y (plan) -> three Z, model Z
 * (height) -> three Y.
 */
export type SectionAxis = 'x' | 'y' | 'z';

const AXIS_NORMALS: Record<SectionAxis, Vector3> = {
  x: new Vector3(1, 0, 0),
  y: new Vector3(0, 0, 1),
  z: new Vector3(0, 1, 0),
};

export function axisNormal(axis: SectionAxis): Vector3 {
  return AXIS_NORMALS[axis].clone();
}

/**
 * Build a clipping plane cutting at `offset` along the model axis. Unflipped,
 * the side below the cut (toward -axis) is kept; three.js clips fragments
 * with negative distance to the plane.
 */
export function buildSectionPlane(axis: SectionAxis, flip: boolean, offset: number): Plane {
  const normal = axisNormal(axis).multiplyScalar(flip ? 1 : -1);
  const pointOnPlane = axisNormal(axis).multiplyScalar(offset);
  return new Plane(normal, -normal.dot(pointOnPlane));
}

export function sectionRange(box: Box3, axis: SectionAxis): { min: number; max: number } {
  const normal = AXIS_NORMALS[axis];
  const min = box.min.dot(normal);
  const max = box.max.dot(normal);
  return { min, max };
}
